import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [params] = useSearchParams();
    const nav = useNavigate();
    const { login } = useAuth();

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const u = await login(email, password);
            const next = params.get("next");
            if (next) nav(next);
            else if (u.role === "admin") nav("/admin");
            else if (u.role === "creator") nav("/creator");
            else nav("/catalog");
        } catch (e) {
            setErr(e.message || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    const quickFill = (role) => {
        if (role === "admin") {
            setEmail("admin@silverrights.io");
            setPassword("Silver@2026");
        } else if (role === "creator") {
            setEmail("creator@silverrights.io");
            setPassword("Creator@2026");
        } else {
            setEmail("buyer@silverrights.io");
            setPassword("Buyer@2026");
        }
    };

    return (
        <div className="min-h-screen bg-ink-300">
            <Navbar />
            <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-2 gap-16 items-center">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="text-[11px] tracking-[0.3em] uppercase text-platinum mb-5">
                        Welcome back
                    </div>
                    <h1 className="font-display text-5xl md:text-6xl leading-none">
                        Sign in to the <span className="silver-text">exchange.</span>
                    </h1>
                    <p className="mt-6 text-silver-400 max-w-md">
                        Secure access to the world's first copyright transfer marketplace.
                    </p>

                    <div className="mt-10 space-y-2">
                        <div className="text-xs text-silver-500 uppercase tracking-widest">
                            Demo accounts
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {["buyer", "creator", "admin"].map((r) => (
                                <button
                                    key={r}
                                    type="button"
                                    onClick={() => quickFill(r)}
                                    className="text-xs tracking-widest uppercase px-3 py-2 border border-white/10 hover:border-platinum/40 hover:text-platinum text-silver-300 rounded-sm"
                                    data-testid={`demo-fill-${r}`}
                                >
                                    {r}
                                </button>
                            ))}
                        </div>
                    </div>
                </motion.div>

                <motion.form
                    onSubmit={submit}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="glass p-8 md:p-10 rounded-sm"
                    data-testid="login-form"
                >
                    <div className="space-y-6">
                        <div>
                            <label className="block text-xs uppercase tracking-widest text-silver-400 mb-2">
                                Email
                            </label>
                            <input
                                type="email"
                                required
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-transparent border-b border-white/20 focus:border-platinum focus:outline-none py-2 text-white"
                                data-testid="login-email"
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-widest text-silver-400 mb-2">
                                Password
                            </label>
                            <input
                                type="password"
                                required
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-transparent border-b border-white/20 focus:border-platinum focus:outline-none py-2 text-white"
                                data-testid="login-password"
                            />
                        </div>
                        {err && (
                            <div className="text-sm text-red-400" data-testid="login-error">
                                {err}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-platinum hover:bg-platinum-200 disabled:opacity-50 text-ink-300 font-medium py-3 rounded-sm tracking-wider uppercase text-sm"
                            data-testid="login-submit"
                        >
                            {loading ? "Signing in…" : "Sign in"}
                        </button>
                        <div className="text-sm text-silver-400 text-center">
                            New here?{" "}
                            <Link to="/register" className="text-platinum hover:underline">
                                Create account
                            </Link>
                        </div>
                    </div>
                </motion.form>
            </div>
        </div>
    );
}
